import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Link,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import moment from "moment";
import { isMobile } from "react-device-detect";
import Swal from "sweetalert2";
import TrackingDetailDialog from "./TrackingDetialDialog.";
import PackageEditDialog from "./PackageEditDialog";
import { GetPackageDetailByTracking } from "api/packages";
import { ClearDepartureBatchItems } from "api/departureBatch";
import { BORDER, DIVIDER } from "components/bento/bentoTokens";
import { playCorrectSound, playIncorrectSound } from "utils";

// Checklist of packages that departed a station (Departure Batch) but have
// not been scanned in yet at the currently-selected Scan station. Lets staff
// open the full tracking timeline, correct a tracking number that was
// mis-scanned at the origin (parcels sometimes carry more than one barcode
// sticker), or Clear an item that got onto this batch by mistake and is
// stuck showing here — without leaving the Scan page.
const PendingArrivalDialog = ({ open, onClose, pendingArrivals = [], onChanged = () => {} }) => {
  const { t } = useTranslation();
  const [trackingDetail, setTrackingDetail] = useState(null);
  const [editPackage, setEditPackage] = useState(null);
  const [clearingId, setClearingId] = useState(null);

  const handleEditClick = async (row) => {
    const res = await GetPackageDetailByTracking(row.tracking);
    if (res.status === 200 && res.data) {
      setEditPackage(res.data);
    }
  };

  const handleClearClick = (row) => {
    Swal.fire({
      icon: "question",
      title: t("scan.pendingArrival.confirmClearTitle"),
      html: `<div style="color:#534EED; font-size: 18px; font-weight:700;">${row.tracking}</div>`,
      text: t("scan.pendingArrival.confirmClearText"),
      showCancelButton: true,
      confirmButtonText: t("scan.pendingArrival.clear"),
      cancelButtonText: t("common.cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      setClearingId(row._id);
      ClearDepartureBatchItems(row.batchId, [row._id]).then((res) => {
        setClearingId(null);
        if (res.success) {
          playCorrectSound();
          onChanged();
        } else {
          playIncorrectSound();
          Swal.fire({ icon: "error", title: t("scan.pendingArrival.clearErrorTitle"), text: res.message });
        }
      });
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant="h6" color="secondary">
            {t("scan.pendingArrival.title")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pendingArrivals.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">{t("scan.pendingArrival.empty")}</Typography>
            </Box>
          ) : (
            pendingArrivals.map((row) => {
              const wasCorrected = row.trackingSnapshot && row.trackingSnapshot !== row.tracking;
              return (
                <Box
                  key={row._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    p: "12px 20px",
                    borderBottom: `1px solid ${DIVIDER}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <Link
                        component="button"
                        color="secondary"
                        underline="hover"
                        onClick={() => setTrackingDetail(row.tracking)}
                        sx={{ fontWeight: 700 }}
                      >
                        {row.tracking}
                      </Link>
                      {wasCorrected && (
                        <Chip
                          label={t("scan.pendingArrival.corrected")}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {row.customer?.name}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {t("scan.pendingArrival.departedFrom", {
                        station: row.fromStationId,
                        time: moment(row.departedAt).fromNow(),
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(row)}
                      aria-label={t("common.edit")}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleClearClick(row)}
                      disabled={clearingId === row._id}
                      aria-label={t("scan.pendingArrival.clear")}
                      title={t("scan.pendingArrival.clear")}
                    >
                      <PlaylistRemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${BORDER}` }}>
          <Button variant="text" color="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <TrackingDetailDialog
        open={!!trackingDetail}
        onClose={() => setTrackingDetail(null)}
        trackingNumber={trackingDetail}
      />

      <PackageEditDialog
        open={!!editPackage}
        onClose={() => setEditPackage(null)}
        packageData={editPackage}
        onSuccess={() => {
          setEditPackage(null);
          onChanged();
        }}
      />
    </>
  );
};

export default PendingArrivalDialog;
